// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';

const POLL_MS = 30_000;

/**
 * Public read of an event surface, kept fresh by polling — never a realtime
 * subscription. The timer only runs while the tab is visible; it pauses on
 * hide and refetches immediately on focus. Data is swapped in place, so the
 * division filter, the scroll position and any open page state survive.
 */
export function usePublicLive(fn: string, body: any, opts: { enabled?: boolean } = {}) {
  const enabled = opts.enabled !== false;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const aliveRef = useRef(true);
  const bodyKey = JSON.stringify(body ?? {});

  const load = useCallback(
    async (mode: 'initial' | 'poll' | 'manual' = 'poll') => {
      if (!enabled) return;
      if (mode === 'initial') setLoading(true);
      if (mode === 'manual') setRefreshing(true);
      const { data: res, error } = await invokeEdgeFunction(fn, { body: JSON.parse(bodyKey) });
      if (!aliveRef.current) return;
      if (error) {
        if (mode === 'initial') setErrorCode((error as any)?.code || 'LOAD_ERROR');
      } else if (!res?.ok) {
        if (mode === 'initial') setErrorCode(res?.error?.code || 'LOAD_ERROR');
      } else {
        setErrorCode(null);
        setData(res.data);
        setLastUpdated(new Date());
      }
      if (mode === 'initial') setLoading(false);
      if (mode === 'manual') setRefreshing(false);
    },
    [fn, bodyKey, enabled],
  );

  useEffect(() => {
    aliveRef.current = true;
    if (!enabled) {
      setLoading(false);
      return () => {
        aliveRef.current = false;
      };
    }
    load('initial');

    let timer: number | null = null;
    const start = () => {
      if (timer !== null) return;
      timer = window.setInterval(() => load('poll'), POLL_MS);
    };
    const stop = () => {
      if (timer === null) return;
      window.clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        load('poll');
        start();
      } else {
        stop();
      }
    };
    const onFocus = () => {
      if (document.visibilityState === 'visible') load('poll');
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      aliveRef.current = false;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [load, enabled]);

  return {
    data,
    loading,
    errorCode,
    lastUpdated,
    refreshing,
    refresh: () => load('manual'),
  };
}

/**
 * Division filter that lives in the URL, so any view can be shared with the
 * division already applied. 'all' keeps the URL clean.
 */
export function useDivisionParam(): [string, (v: string) => void] {
  const [params, setParams] = useSearchParams();
  const division = params.get('division') || 'all';
  const setDivision = (value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete('division');
    else next.set('division', value);
    setParams(next, { replace: true });
  };
  return [division, setDivision];
}

/** Append the active division to a public link so it travels between views. */
export function withDivision(path: string, division: string) {
  if (!division || division === 'all') return path;
  return `${path}?division=${encodeURIComponent(division)}`;
}

/** "Updated 2:41:07 PM" text for the live footer. */
export function formatUpdated(d: Date | null) {
  if (!d) return '—';
  return d.toLocaleTimeString();
}
